import type { BookCardBook } from '@/components/books/book-card';
import { BookFilter, type BookSort } from '@/components/books/book-filter';
import { BookList } from '@/components/books/book-list';
import { BookPagination } from '@/components/books/book-pagination';
import { GlobalTopBar } from '@/components/layout/global-top-bar';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from '@/components/ui/breadcrumb';
import { hydratePinnedBooks, usePinnedBookIds } from '@/hooks/use-pinned-books';
import { fetchMaBooksList } from '@/lib/ma-books-api';
import { fetchMaUserConfiguration } from '@/lib/ma-user-config-api';
import { orderPinnedBooks } from '@/lib/pinned-books';
import authService from '@/services/zybook-auth-service';
import { useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';

const BOOKS_PER_PAGE = 16;

const SUBJECT_STUB = 'Uncategorized';

export async function loader() {
    const [books, userConfig] = await Promise.all([fetchMaBooksList(), fetchMaUserConfiguration()]);
    return {
        books: books.map(book => ({ ...book, subject: SUBJECT_STUB })),
        pinnedBookIds: userConfig.pinnedBooks,
    };
}

// By default, react router runs loader(){} when there are url changes,
// our search/filtering uses the url as state, which leads to loader()
// triggering on every filter change.
// shouldRevalidate is a react router hook that lets us opt out of this.
// The reason for this is because all books will be loaded via fetchMaBooksList()
export function shouldRevalidate() {
    return false;
}

const SORT_VALUES: BookSort[] = ['title-asc', 'title-desc', 'subject'];

function parseSortParam(value: string | null): BookSort {
    return value && SORT_VALUES.includes(value as BookSort) ? (value as BookSort) : 'title-asc';
}

function parseSubjectParam(value: string | null, validSubjects: string[]): string {
    if (!value || value === 'all') {
        return 'all';
    }
    return validSubjects.includes(value) ? value : 'all';
}

function applyBooksSearchToParams(prev: URLSearchParams, state: { search: string; subject: string; sort: BookSort }) {
    const next = new URLSearchParams(prev);
    if (state.search.trim()) {
        next.set('search', state.search.trim());
    } else {
        next.delete('search');
    }
    if (state.subject !== 'all') {
        next.set('subject', state.subject);
    } else {
        next.delete('subject');
    }
    if (state.sort !== 'title-asc') {
        next.set('sort', state.sort);
    } else {
        next.delete('sort');
    }
    next.delete('view');
    return next;
}

export default function Books() {
    const { books: loaderBooks, pinnedBookIds } = useLoaderData() as {
        books: BookCardBook[];
        pinnedBookIds: string[];
    };
    hydratePinnedBooks(pinnedBookIds);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const pinnedIds = usePinnedBookIds();

    const subjects = Array.from(
        new Set(loaderBooks.map(book => book.subject).filter((label): label is string => Boolean(label?.trim()))),
    ).sort();

    const search = searchParams.get('search') ?? '';
    const subject = parseSubjectParam(searchParams.get('subject'), subjects);
    const sort = parseSortParam(searchParams.get('sort'));

    const pinnedBooksOrdered = orderPinnedBooks(loaderBooks, pinnedIds);

    const normalizedSearch = search.trim().toLowerCase();
    const filteredBooks = loaderBooks
        .filter(book => {
            const bookSubject = (book.subject ?? '').toLowerCase();
            const matchesSearch = book.title.toLowerCase().includes(normalizedSearch) || bookSubject.includes(normalizedSearch);
            const matchesSubject = subject === 'all' || book.subject === subject;

            return matchesSearch && matchesSubject;
        })
        .sort((firstBook, secondBook) => {
            if (sort === 'title-desc') {
                return secondBook.title.localeCompare(firstBook.title);
            }

            if (sort === 'subject') {
                return (firstBook.subject ?? '').localeCompare(secondBook.subject ?? '') || firstBook.title.localeCompare(secondBook.title);
            }

            return firstBook.title.localeCompare(secondBook.title);
        });

    const totalPages = Math.max(1, Math.ceil(filteredBooks.length / BOOKS_PER_PAGE));
    const visiblePage = Math.min(currentPage, totalPages);
    const pageStart = (visiblePage - 1) * BOOKS_PER_PAGE;
    const visibleBooks = filteredBooks.slice(pageStart, pageStart + BOOKS_PER_PAGE);
    const showingFrom = filteredBooks.length === 0 ? 0 : pageStart + 1;
    const showingTo = filteredBooks.length === 0 ? 0 : pageStart + visibleBooks.length;
    const canManageToc = authService.canSeeAllMAContent();

    function updateSearch(nextSearch: string) {
        setSearchParams(
            prev =>
                applyBooksSearchToParams(prev, {
                    search: nextSearch,
                    subject,
                    sort,
                }),
            { replace: true },
        );
        setCurrentPage(1);
    }

    function updateSort(nextSort: BookSort) {
        setSearchParams(
            prev =>
                applyBooksSearchToParams(prev, {
                    search,
                    subject,
                    sort: nextSort,
                }),
            { replace: true },
        );
        setCurrentPage(1);
    }

    return (
        <>
            <GlobalTopBar.Breadcrumbs>
                <Breadcrumb className="min-w-0 truncate">
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbPage className="font-bold">Books</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </GlobalTopBar.Breadcrumbs>

            <main className="flex flex-col items-center w-vh h-full min-h-0 p-6 overflow-auto">
                <div className="w-full max-w-6xl space-y-6">
                    <div>
                        <h1 className="mt-2 text-3xl font-bold">Books</h1>
                    </div>

                    {canManageToc && pinnedBooksOrdered.length > 0 ? (
                        <section
                            className="space-y-2"
                            aria-label="Pinned books"
                        >
                            <h2 className="text-lg font-semibold">Pinned</h2>
                            <BookList
                                books={pinnedBooksOrdered}
                                layout="pinned"
                                showPin
                            />
                        </section>
                    ) : null}

                    <BookFilter
                        search={search}
                        sort={sort}
                        onSearchChange={updateSearch}
                        onSortChange={updateSort}
                    />

                    <div className="text-muted-foreground text-sm">
                        {filteredBooks.length === 0 ? (
                            <span>No books match your filters.</span>
                        ) : (
                            <span>
                                Showing {showingFrom}–{showingTo} of {filteredBooks.length} books
                            </span>
                        )}
                    </div>

                    <BookList
                        books={visibleBooks}
                        showPin={canManageToc}
                    />

                    <BookPagination
                        currentPage={visiblePage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </main>
        </>
    );
}
